import memoize from 'lodash/memoize';
import reactReplacePlaceholder from 'react-replace-placeholder';

import makePluralKey, {
  DEFAULT_MAX_VARIANTS,
  DEFAULT_PLURAL_SUFFIX,
} from './make-translations-plural-key';

import type { TranslationKey } from '@shared/constants/translations';
import type {
  TranslationServiceType,
  Translate,
  TranslatePlural,
} from '@shared/types/i18n/translation-service';

const TranslationService: TranslationServiceType = ({
  logger,
  translations,
}) => {
  const translate: Translate = (
    key: TranslationKey,
    replacements = {},
    startTag = '{',
    endTag = '}',
  ) => {
    const message = translations[key];
    if (!message) {
      // Only log if key is not undefined/null to avoid logging undefined
      if (key) {
        logger.warn(`Missing translation for key "${key}"`);
      }
      return '';
    }
    if (!replacements || Object.keys(replacements).length === 0) {
      return message;
    }
    if (
      Object.values(replacements).some(
        (v) => v !== null && typeof v === 'object',
      )
    ) {
      throw new Error(
        'For replacements containing objects (React component or html element), use the FormattedMessage component instead',
      );
    }

    return memoize(reactReplacePlaceholder)(
      message,
      replacements,
      startTag,
      endTag,
    ).join('');
  };

  const translatePlural: TranslatePlural = (
    key,
    cardinality,
    replacementKey,
    extraReplacements = {},
    maxVariants = DEFAULT_MAX_VARIANTS,
    extra = DEFAULT_PLURAL_SUFFIX,
    startTag = '{',
    endTag = '}',
  ) => {
    const pluralKey = makePluralKey(key, cardinality, maxVariants, extra);

    const replacements = { ...(extraReplacements || {}) };
    if (cardinality > maxVariants) {
      replacements[replacementKey] = cardinality;
    }
    return translate(
      pluralKey as TranslationKey,
      replacements,
      startTag,
      endTag,
    );
  };

  return {
    translate,
    translatePlural,
  };
};

export default TranslationService;
